import httpClient from "services/network/httpClient";
import { EmployeeData } from "./NewEmployeeDetail";
import { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faUser,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faIdCard,
  faGlobe,
  faSpinner,
  faExclamationCircle,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

interface AssignDistributorsProps {
  employee: EmployeeData;
}

interface Distributor {
  id: number;
  distributorName: string;
  email: string;
  phone: string;
  address: string;
  contactPerson: string;
  customerCode: string;
  village: string;
  city: string;
  postalCode: string;
  state: string;
  country: string;
}

const AssignDistributors = ({ employee }: AssignDistributorsProps) => {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const listOfDistributors = useCallback(async () => {
    console.log("Fetching distributors for employee ID: ", employee.id);
    setLoading(true);
    setError(null);

    try {
      const res: any = await httpClient.get(`/distributor/users/${employee.id}/distributors`);
      console.log("Response from API: ", res);
      setDistributors(res.value || res || []);
    } catch (error) {
      console.error("Error fetching distributors: ", error);
      setError("Failed to load distributors. Please try again later.");
      setDistributors([]);
    } finally {
      setLoading(false);
    }
  }, [employee.id]);

  useEffect(() => {
    if (employee.id) {
      listOfDistributors();
    }
  }, [employee.id, listOfDistributors]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-5xl animate-spin mb-4"
        />
        <p className="text-gray-600 font-medium">Loading distributors...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-red-50 rounded-xl border-2 border-red-200">
        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-red-500 text-5xl mb-4"
        />
        <p className="text-red-600 font-semibold mb-2">Error Loading Distributors</p>
        <p className="text-gray-600 text-sm mb-4">{error}</p>
        <button
          onClick={listOfDistributors}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
        >
          Retry
        </button>
      </div>
    );
  }

  if (distributors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
        <FontAwesomeIcon icon={faUsers} className="text-gray-400 text-5xl mb-4" />
        <p className="text-gray-600 font-semibold mb-1">No Distributors Assigned</p>
        <p className="text-gray-500 text-sm">
          This employee doesn't have any assigned distributors yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-5 rounded-xl shadow-md flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-white/20 p-3 rounded-lg mr-4">
            <FontAwesomeIcon icon={faUsers} className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-white font-bold text-xl">Assigned Distributors</h3>
            <p className="text-indigo-100 text-sm">
              {distributors.length} {distributors.length === 1 ? "distributor" : "distributors"} assigned
            </p>
          </div>
        </div>
      </div>

      {/* Distributors Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-h-[500px] overflow-auto">
        {distributors.map((distributor) => (
          <div
            key={distributor.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/40 hover:shadow-xl dark:hover:shadow-gray-900/60 transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-500 overflow-hidden"
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-bold text-lg mb-1 flex items-center">
                    <FontAwesomeIcon icon={faBuilding} className="mr-2 text-indigo-200" />
                    {distributor.distributorName}
                  </h4>
                  <div className="flex items-center">
                    <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full font-semibold">
                      <FontAwesomeIcon icon={faIdCard} className="mr-1" />
                      {distributor.customerCode}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-5 space-y-3">
              {/* Contact Person */}
              <div className="flex items-start hover:bg-indigo-50 p-2 rounded-lg transition-colors">
                <div className="bg-indigo-100 p-2 rounded-lg mr-3 flex-shrink-0">
                  <FontAwesomeIcon icon={faUser} className="text-indigo-600 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                    Contact Person
                  </div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200">
                    {distributor.contactPerson || "N/A"}
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start hover:bg-green-50 p-2 rounded-lg transition-colors">
                <div className="bg-green-100 p-2 rounded-lg mr-3 flex-shrink-0">
                  <FontAwesomeIcon icon={faPhone} className="text-green-600 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                    Phone
                  </div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200">
                    {distributor.phone || "N/A"}
                  </div>
                </div>
              </div>

              {/* Email */}
              {distributor.email && (
                <div className="flex items-start hover:bg-blue-50 p-2 rounded-lg transition-colors">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3 flex-shrink-0">
                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                      Email
                    </div>
                    <div className="font-semibold text-gray-800 dark:text-gray-200 break-all">
                      {distributor.email}
                    </div>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="flex items-start hover:bg-purple-50 p-2 rounded-lg transition-colors">
                <div className="bg-purple-100 p-2 rounded-lg mr-3 flex-shrink-0">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-purple-600 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                    Location
                  </div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200">
                    {[
                      distributor.village,
                      distributor.city,
                      distributor.state,
                      distributor.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </div>
                </div>
              </div>

              {/* Address */}
              {distributor.address && (
                <div className="flex items-start hover:bg-amber-50 p-2 rounded-lg transition-colors">
                  <div className="bg-amber-100 p-2 rounded-lg mr-3 flex-shrink-0">
                    <FontAwesomeIcon icon={faBuilding} className="text-amber-600 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                      Address
                    </div>
                    <div className="font-medium text-gray-800 dark:text-gray-200 text-sm break-words">
                      {distributor.address}
                    </div>
                  </div>
                </div>
              )}

              {/* Country */}
              {distributor.country && (
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-400 p-2 rounded-lg">
                  <div className="flex items-center text-gray-600 text-sm">
                    <FontAwesomeIcon icon={faGlobe} className="mr-2" />
                    <span className="font-medium">{distributor.country}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssignDistributors;